/**
 * Opens a tab now and sends it somewhere later.
 *
 * A document URL is minted by an API call, so it does not exist at the moment
 * of the click. Opening the tab after the call returns puts the open outside
 * the user gesture and popup blockers stop it. So we claim the tab on the click
 * and navigate it when the URL arrives, falling back to this tab if the browser
 * refused the first step.
 *
 * The URL passed to navigate is treated as opaque. Nothing here reads it,
 * rewrites it or keeps it.
 */

export interface PendingTab {
  navigate: (url: string) => void;
  cancel: () => void;
}

export function openPendingTab(): PendingTab {
  let handle: Window | null = null;
  try {
    handle = window.open('', '_blank');
  } catch {
    handle = null;
  }

  if (handle) {
    /* Same origin, so this is tidiness rather than isolation. */
    try {
      handle.opener = null;
    } catch {
      /* Some browsers refuse the assignment. Not worth failing the open over. */
    }
  }

  return {
    navigate(url: string) {
      if (handle && !handle.closed) {
        handle.location.href = url;
      } else {
        window.location.assign(url);
      }
    },
    cancel() {
      if (handle && !handle.closed) {
        handle.close();
      }
    },
  };
}
